/**
 * meslekler.js — bir kişinin BİRDEN ÇOK mesleği için ortak okuma yardımcıları (backlog #372, backend #371).
 *
 * Backend #371 mesleği FUAR BAŞINA tutar (Deniz'in kararı: ileride firma başına geri gelebilir): fuarlı satırda
 *   · `meslekler[{ meslekId, meslekAdi, buFirmada, belgeKurali }]` — kişinin o fuardaki TÜM meslekleri, her firmanın verdiği;
 *     `buFirmada` = bakılan firmanın kendi verdiği (başka firmanınki false, adı verilmez; fuar geneli listede null),
 *   · `meslekId` / `meslekName` — bunların ilki ("Kaynakçı +N" → N = meslekler.length - 1),
 *   · `meslekiEgitimler[{ meslekId, meslekAdi, istenen, sayilir, belge }]` — her mesleğin sertifika yuvası,
 *   · `etiketsizMeslekiEgitimBelgeleri[]` — 2+ meslek sertifika isterken hangisi olduğu söylenmemiş, hiçbirine sayılmayan belgeler,
 *   · `documents.meslekiEgitim` — istenenlerin EN KÖTÜSÜ (liste karesi; anahtarlar değişmedi).
 * Fuarsız satırda (Tüm Kişiler) üçü de null; `firmalar[].meslekler[{ meslekId, meslekAdi }]` o firmanın işe alımlarındaki mesleklerdir.
 *
 * Bu dosya yalnızca veriyi okur; renk/ikon/çizim ilgili bileşenlerde.
 */

/** Satırın (ya da verilen firma girdisinin) meslek listesi; hiçbiri yoksa []. Yalnızca adı olan girdiler. */
export function satirMeslekleri(row, firma = null) {
  const liste = row?.meslekler?.length ? row.meslekler : firma?.meslekler;
  return (liste || []).filter((m) => m && m.meslekId);
}

/**
 * Vesikalığın altındaki "Kaynakçı +1" etiketi: `{ ad, ek, adlar }` ya da meslek yoksa null.
 * Fuarlı satırda `meslekler[]`, fuarsızda (Tüm Kişiler) öndeki firmanın işe alım meslekleri; ikisi de yoksa eski tek ad
 * (satırın kişi kartındaki mesleği, o da yoksa firma girdisinin işe alım mesleği).
 */
export function meslekEtiketi(row, firma = null) {
  const liste = satirMeslekleri(row, firma);
  if (liste.length > 0) {
    const adlar = liste.map((m) => m.meslekAdi).filter(Boolean);
    const ad = (liste[0].meslekAdi || row?.meslekName || firma?.meslekName) ?? null;
    return ad ? { ad, ek: Math.max(0, liste.length - 1), adlar } : null;
  }
  const ad = row?.meslekName || firma?.meslekName || null;
  return ad ? { ad, ek: 0, adlar: [ad] } : null;
}

/** Satırın Mesleki Eğitim yuvaları; birden çok meslek yoksa (ya da fuarsızsa) null → tek sertifika gibi davranılır. */
export function meslekiYuvalar(row) {
  const liste = row?.meslekiEgitimler;
  return Array.isArray(liste) && liste.length > 0 ? liste : null;
}

/** Hiçbir mesleğe sayılmayan etiketsiz sertifikalar; yoksa []. */
export function etiketsizSertifikalar(row) {
  return Array.isArray(row?.etiketsizMeslekiEgitimBelgeleri) ? row.etiketsizMeslekiEgitimBelgeleri : [];
}

// ─── #466 (backend #464): meslek KİŞİNİNDİR ──────────────────────────────────────────────────────────────────────
// `meslekler[]` fuardan ve firmadan bağımsız kişinin tuttuğu meslekleri taşır (fuarsız da dolu); her biri kimin eklediğini
// (`kaynak` SAKA | FIRMA | KISI, `ekleyenFirmaAdi` — başka firmanın adı firma girişinde null), `onayli`yı ve kategorisini söyler.
// `kartMeslekleri[]` kartın "Çalışan" dışında hangi meslekle basılabileceğini söyler (kural backend'de: burada türetilmez).

/** Kişinin meslekleri (kaldırılmış meslek dahil, kaynağı olan her girdi); yoksa []. */
export function kisiMeslekleri(row) {
  return (row?.meslekler || []).filter((m) => m && m.meslekId);
}

/** `kartMeslekleri[]` içinden bir mesleğin girdisi ya da null. */
export function kartMeslegiOf(row, meslekId) {
  return (row?.kartMeslekleri || []).find((k) => Number(k.meslekId) === Number(meslekId)) ?? null;
}

/**
 * "Kimin eklediği" metni: SAKA · bu firma · adı bilinen firma · başka (adı gizli) firma · kişinin kendisi.
 * `t` = `components` ad alanının çeviri fonksiyonu.
 */
export function meslekKaynakMetni(m, t) {
  if (m.kaynak === "SAKA") return t("kisiMeslekleri.kaynakSaka");
  if (m.kaynak === "KISI") return t("kisiMeslekleri.kaynakKisi");
  if ((m.buFirmaEkledi ?? m.buFirmada) === true) return t("kisiMeslekleri.kaynakBuFirma");
  if (m.ekleyenFirmaAdi) return t("kisiMeslekleri.kaynakFirma", { firma: m.ekleyenFirmaAdi });
  return t("kisiMeslekleri.kaynakBaskaFirma");
}

const SIRA_SONA = Number.MAX_SAFE_INTEGER;
const sirala = (a, b, dil) => ((a.sira ?? SIRA_SONA) - (b.sira ?? SIRA_SONA)) || String(a.ad ?? "").localeCompare(String(b.ad ?? ""), dil);

/**
 * `GET /meslek` düz listesini seçici için kategoriye göre gruplar (#463 ağacı, #466): `[{ kategori, kategoriHaric, meslekler }]`.
 * `kategori` = kategori satırı (kendisi de seçilebilir) ya da kategorisiz grup için null (en sonda). Kaldırılmış satırlar ve `meslekId 0`
 * (varsayılan kapsam) atılır; `haric` içindeki id'ler (kişide zaten olanlar) seçenek olmaz — ama bir kategori `haric`se işleri yine
 * kendi grubunda kalır (`kategoriHaric` true: kategori satırı seçenek olarak sunulmaz). Boş grup çıkmaz.
 */
export function mesleklerKategoriyeGore(katalog, { haric = [], dil = "tr" } = {}) {
  const dislanan = new Set(haric.map(Number));
  const uygun = (katalog || []).filter((m) => m && Number(m.meslekId) !== 0 && !m.kaldirildi);
  const kategoriler = uygun.filter((m) => m.kategori === true).sort((a, b) => sirala(a, b, dil));
  const isler = uygun.filter((m) => m.kategori !== true && !dislanan.has(Number(m.meslekId)));
  const gruplar = kategoriler
    .map((k) => ({
      kategori: k,
      kategoriHaric: dislanan.has(Number(k.meslekId)),
      meslekler: isler.filter((m) => Number(m.parentId) === Number(k.meslekId)).sort((a, b) => sirala(a, b, dil)),
    }))
    .filter((g) => !g.kategoriHaric || g.meslekler.length > 0);
  const kategorisiz = isler.filter((m) => !kategoriler.some((k) => Number(k.meslekId) === Number(m.parentId))).sort((a, b) => sirala(a, b, dil));
  if (kategorisiz.length > 0) gruplar.push({ kategori: null, kategoriHaric: false, meslekler: kategorisiz });
  return gruplar;
}

/**
 * Bir kişi satırının, o satırın kartı yazdırma kararının AÇIKLADIĞI engellerden BAŞKA, bir mesleği kartta yazılamaz kılan
 * eksikleri: `[{ meslekAdi, kodlar }]`. Örn. Mesleki Eğitim eksik: kart yine "Çalışan" olarak basılır (#466) — bu bir engel
 * değil bilgi satırıdır. `uygunsuzluklar` içinde zaten sayılan kod (ör. `BELGE:sgk`) tekrar yazılmaz. Karar backend'in:
 * `kartMeslekleri[].eksikler` olduğu gibi taşınır, burada hesaplanmaz.
 */
export function meslekBilgiEksikleri(kisi, uygunsuzluklar = []) {
  const sayilan = new Set(uygunsuzluklar);
  return (kisi?.kartMeslekleri || [])
    .filter((k) => !k.basilabilir)
    .map((k) => ({ meslekAdi: k.meslekAdi, kodlar: (k.eksikler || []).filter((kod) => !sayilan.has(kod)) }))
    .filter((k) => k.kodlar.length > 0);
}

/**
 * Bir kayıt okumasından (tek kişi satırı: crew üyesi / kart-karari `kisi`si) DÜZENLENECEK meslek kümesi (#372):
 * `{ ids, digerleri, adlar }`. `ids` = bu işe alımın kendi verdikleri (`buFirmada !== false`), `digerleri` = öteki firmaların
 * verdikleri (salt gösterim), `adlar` = id → ad. `meslekler[]` yoksa (eski yanıt / okuma düştü) tek `meslekId`'ye düşülür.
 * Kaynak KISI iken kişi kartındaki meslek düzenlenebilir ilk küme sayılır (hiçbir işe alım vermemiş, eskisi gibi).
 */
export function duzenlenecekKume(kayit, yedek = {}) {
  const liste = (kayit?.meslekler || []).filter((m) => m && m.meslekId);
  const adlar = {};
  if (liste.length > 0) {
    liste.forEach((m) => { adlar[m.meslekId] = m.meslekAdi; });
    const kisiKarti = kayit.meslekKaynak === 'KISI';
    return {
      ids: liste.filter((m) => kisiKarti || m.buFirmada !== false).map((m) => Number(m.meslekId)),
      digerleri: kisiKarti ? [] : liste.filter((m) => m.buFirmada === false),
      adlar,
    };
  }
  const meslekId = Number(kayit?.meslekId ?? yedek.meslekId) || 0;
  if (meslekId) adlar[meslekId] = kayit?.meslekName ?? yedek.meslekName ?? null;
  return { ids: meslekId ? [meslekId] : [], digerleri: [], adlar };
}

/** İki meslek kümesi (sırasız) aynı mı? */
export function ayniKume(a, b) {
  return a.length === b.length && [...a].map(Number).sort((x, y) => x - y).join() === [...b].map(Number).sort((x, y) => x - y).join();
}

// ─── #467 (backend #396): Kart Yazdır'da kartta yazacak meslek ───────────────────────────────────────────────────
// `kartMeslekleri[]` + `kartCalisanBasilabilir` kart-karari girdisinde gelir (backend hesaplar: burada türetilmez). Kartta yazan
// ya "Çalışan" (meslekId 0 / null) ya da kişinin tuttuğu ve o firma girdisinde basılabilir bir meslek.

/** POST /kart-yazdir `meslekId`si: 0 = kesin Çalışan. */
export const CALISAN_DEGERI = 0;

/**
 * Kart Yazdır'ın seçenekleri: önce Çalışan, sonra kişinin tuttuğu her meslek. Kişinin hiç mesleği yoksa seçilecek bir şey de yoktur → [].
 * `basilabilir` false olan seçenek seçilemez; `eksikler` nedenini taşır (`kartMeslekleri[].eksikler` kodları, Çalışan için boş:
 * kart-karari Çalışan setinin eksiklerini ayrıca listelemez, yalnızca `kartCalisanBasilabilir` der).
 * @returns {{ deger: number, calisan: boolean, ad: string|null, kategoriAdi: string|null, basilabilir: boolean, eksikler: string[] }[]}
 */
export function kartMeslekSecenekleri(kisi) {
  const meslekler = (kisi?.kartMeslekleri || []).filter((k) => k && Number(k.meslekId) > 0);
  if (meslekler.length === 0) return [];
  return [
    // `!== false`: eski/eksik yanıt Çalışan'ı engellemesin (KartUygunsuzluklari.MeslekBilgisi ile aynı okuma).
    { deger: CALISAN_DEGERI, calisan: true, ad: null, kategoriAdi: null, basilabilir: kisi?.kartCalisanBasilabilir !== false, eksikler: [] },
    ...meslekler.map((k) => ({
      deger: Number(k.meslekId),
      calisan: false,
      ad: k.meslekAdi ?? null,
      kategoriAdi: k.kategoriAdi ?? null,
      basilabilir: Boolean(k.basilabilir),
      eksikler: k.eksikler || [],
    })),
  ];
}

/**
 * Seçili değer: kullanıcının seçimi hâlâ basılabilir bir seçenekse o, yoksa ilk basılabilir (Çalışan sıranın başında, yani
 * "Çalışan varsayılan"; Çalışan basılamıyorsa ilk basılabilir meslek), hiçbiri basılabilir değilse Çalışan (düğme zaten kapalıdır).
 */
export function secilenKartSecenegi(secenekler, kullaniciSecimi = null) {
  const secilen = (secenekler || []).find((s) => s.deger === kullaniciSecimi && s.basilabilir)
    ?? (secenekler || []).find((s) => s.basilabilir)
    ?? (secenekler || [])[0]
    ?? null;
  return secilen;
}

/**
 * Basılan (ya da geçmişteki) bir kartta ne yazıyor: `data.kartBasim` / `kart-basimlari` satırı → `{ calisan, ana, ince }`.
 * Karar (#464, Deniz 2026-10-19): kategori yazılır, yaprak (meslek) küçük puntoyla; kategorisiz meslekte yalnızca meslek.
 */
export function kartBasimYazisi(kartBasim) {
  if (!kartBasim || kartBasim.meslekId == null || Number(kartBasim.meslekId) === 0) return { calisan: true, ana: null, ince: null };
  const meslek = kartBasim.meslekAdi ?? `#${kartBasim.meslekId}`;
  const kategori = kartBasim.kategoriAdi && kartBasim.kategoriAdi !== meslek ? kartBasim.kategoriAdi : null;
  return { calisan: false, ana: kategori ?? meslek, ince: kategori ? meslek : null };
}
